import { defineStore } from '@/utils/store'
import axios, { type AxiosError } from 'axios'
import { createUUID } from '@/utils'
import { useInterval, useMemoizedFn } from 'ahooks'
import { getResponseError } from '@apis'
import i18n from '@/i18n'

/** 自動關閉時間 */
const AUTO_CLOSE = 5 * 1000

/** 消失動畫時間 */
const ANIMATION_LEAVE = 250

/** 最多同時幾個 */
const MAX_TOAST = 3

/** 型別 */
export type ToastItem = {
  id: string
  // 提示訊息
  message: string
  title?: string
  type?: 'danger' | 'success'
  // 隱藏時間
  expired: number
  // 啟用狀態
  enabled: boolean
}

type State = {
  // 全部提示訊息
  toasts: ToastItem[]
  // 全螢幕 loading
  loading: number[]
}

/** Toast Store */
const [useToastStore, setToastState] = defineStore<State>(
  {
    // 全部提示訊息
    toasts: [],
    // 全螢幕 loading，把時間當 id 即可，時間到會消失
    loading: [], // 要自己在根層掛組件
  },
  'TOAST',
)
export { useToastStore, setToastState }

/** 新增 Toast */
export const toast = (message?: (string | number) | Pick<ToastItem, 'message' | 'title' | 'type'>, autoClose = AUTO_CLOSE, max = MAX_TOAST) => {
  const isMessageObj = typeof message === 'object' && message !== null

  setToastState((state) => {
    state.toasts.push({
      id: createUUID(),
      ...(isMessageObj ? message : { message: String(message) || '' }),
      expired: Date.now() + autoClose,
      enabled: true,
    })
    const enable = state.toasts.filter((item) => item.enabled)
    if (enable.length > max) enable[0].enabled = false
  })
}

/** 自動套用 i18n */
export const toastI18n = (key: string, autoClose = AUTO_CLOSE, max = MAX_TOAST) => toast(i18n.t(key), autoClose, max)

/** 自動確認 catch error 是否有訊息 */
export const errorToast = (error: AxiosError<any> | unknown, autoClose = AUTO_CLOSE) => {
  if (typeof error !== 'object' || error === null) return // 不是物件
  if (axios.isCancel(error) as unknown) return // 被取消
  toast(getResponseError(error, String(error)), autoClose)
}

/** 關閉某一筆 */
export const removeToast = (id: string) =>
  setToastState((state) => {
    const find = state.toasts.find((item) => item.id === id)
    if (!find) return
    find.expired = Date.now() - 1
    find.enabled = false
  })

/** 清理 toast。需要輪詢呼叫 */
export const clearToast = () => {
  const now = Date.now()
  const toasts = useToastStore.getState().toasts.slice(0)
  // 甭更新
  if (!toasts.some((item) => (item.enabled && item.expired < now) || item.expired + ANIMATION_LEAVE < now)) return
  // 更新
  setToastState((state) => {
    for (let index = state.toasts.length - 1; index >= 0; --index) {
      const item = state.toasts[index]
      if (item.expired < now) item.enabled = false
      if (item.expired + ANIMATION_LEAVE > now) continue
      state.toasts.splice(index, 1)
    }
  })
}

/**
 * toast 需要掛載的 hooks
 */
export const useToast = () => {
  const toasts = useToastStore((state) => state.toasts)

  /** 自動關閉 */
  useInterval(clearToast, toasts.length > 0 ? 200 : undefined, {
    immediate: toasts.length > 0,
  })

  return { toasts }
}

/**
 * 設定全螢幕 loading
 * 把時間當 key 即可，時間到會消失
 */
export const setLoading = (time?: number) => {
  const key = time || Date.now() + 60 * 1000
  setToastState((state) => void state.loading.push(key))
  return key
}

/** 清除全螢幕 loading */
export const clearLoading = (time?: number) => {
  setToastState((state) => {
    if (!time) {
      state.loading = []
    } else {
      state.loading = state.loading.filter((item) => item !== time)
    }
  })
}

/**
 * loading 需要掛載的定時清理
 */
export const useLoading = () => {
  const enabled = useToastStore((state) => !!state.loading?.length)

  /** 定時清理 */
  const onClear = useMemoizedFn(() =>
    setToastState((state) => {
      state.loading = state.loading.filter((item) => item > Date.now())
    }),
  )

  /** 自動關閉 */
  useInterval(onClear, enabled ? 1000 : undefined, {
    immediate: enabled,
  })

  return { enabled }
}

/**
 * 獲取 loading 狀態 - 監控是否正在 loading
 */
export const useIsLoading = (): boolean => useToastStore((state) => state.loading.length > 0)
